import { useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Plus, Trash2, ChevronUp, ChevronDown, ChevronRight } from "lucide-react";
import { projectsApi } from "@/services/projectsApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DescriptionField } from "./DescriptionField";
import { SectionSourcesSummary } from "./SectionSourcesSummary";
import type { GridReviewMap } from "@/lib/extractionReview";
import type { Document } from "@/types/models";

type ProjectTemplateData = Awaited<ReturnType<typeof projectsApi.getProjectTemplate>>;

export interface Section {
  id: string;
  name: string;
  priming: string | null;
  description: string | null;
  description_context: string | null;
  description_ai_output: string | null;
  parent_id: string | null;
  ordinal: number | null;
}

export interface SectionSources {
  documentIds: string[];
  extractionPointIds: string[];
  artifactIds: string[];
}

interface SortableRowProps {
  section: Section;
  label: string;
  isFirst: boolean;
  isLast: boolean;
  isChild: boolean;
  projectId: string;
  templateQueryKey: string[];
  sectionSources: SectionSources;
  documents: Document[];
  extractionPoints: { id: string; name: string; parent_id?: string | null }[];
  artifacts?: { uid: string; name: string; type: string }[];
  gridReview: GridReviewMap;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDelete: () => void;
  onAddChild: () => void;
  onOpenSources: () => void;
  isCollapsible?: boolean;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  /** Extra column cell rendered after Sources, before Actions */
  extraColumn?: ReactNode;
  /** Content rendered below the row when a section has been generated */
  generatedContent?: ReactNode;
  /** Hide the Actions column (move up/down/add/delete buttons) */
  hideActions?: boolean;
  /** Highlight the row in red when no sources are selected */
  highlightNoSources?: boolean;
}

export const SortableRow = ({
  section,
  label,
  isFirst,
  isLast,
  isChild,
  projectId,
  templateQueryKey,
  sectionSources,
  documents,
  extractionPoints,
  artifacts,
  gridReview,
  onMoveUp,
  onMoveDown,
  onDelete,
  onAddChild,
  onOpenSources,
  isCollapsible,
  isCollapsed,
  onToggleCollapse,
  extraColumn,
  generatedContent,
  hideActions,
  highlightNoSources,
}: SortableRowProps) => {
  const queryClient = useQueryClient();
  const [nameValue, setNameValue] = useState(section.name);

  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } =
    useSortable({ id: section.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleNameBlur = async () => {
    const trimmed = nameValue.trim();
    if (!trimmed || trimmed === section.name) return;

    const queryKey = templateQueryKey;
    queryClient.setQueryData<ProjectTemplateData>(queryKey, (old) => {
      if (!old) return old;
      return {
        ...old,
        prompt_libraries: old.prompt_libraries.map((pl) =>
          pl.id === section.id ? { ...pl, name: trimmed } : pl
        ),
      };
    });

    try {
      await projectsApi.updateProjectPromptLibrary(projectId, section.id, { name: trimmed });
    } catch {
      setNameValue(section.name);
      queryClient.setQueryData<ProjectTemplateData>(queryKey, (old) => {
        if (!old) return old;
        return {
          ...old,
          prompt_libraries: old.prompt_libraries.map((pl) =>
            pl.id === section.id ? { ...pl, name: section.name } : pl
          ),
        };
      });
    }
  };

  const hasNoSources = highlightNoSources &&
    sectionSources.documentIds.length === 0 &&
    sectionSources.extractionPointIds.length === 0 &&
    sectionSources.artifactIds.length === 0;

  return (
    <div ref={setNodeRef} style={style}>
      <div className={`flex items-start gap-4 border-b p-4 ${hasNoSources ? "bg-red-50" : ""}`}>
      {/* Left group: drag handle + number + content — only this section indents for children */}
      <div className={`flex items-start gap-4 flex-1 min-w-0 ${isChild ? "pl-6" : ""}`}>
        {/* Drag handle */}
        <button
          ref={setActivatorNodeRef}
          {...attributes}
          {...listeners}
          className="mt-0.5 cursor-grab active:cursor-grabbing text-muted-foreground"
          tabIndex={-1}
        >
          <GripVertical className="w-4 h-4" />
        </button>

        {/* Collapse toggle — fixed-width slot keeps alignment for non-collapsible rows */}
        <span className="mt-0.5 w-4 shrink-0 flex items-center justify-center">
          {isCollapsible && (
            <button
              onClick={onToggleCollapse}
              className="text-muted-foreground hover:text-foreground transition-colors"
              tabIndex={-1}
              title={isCollapsed ? "Expand" : "Collapse"}
            >
              {isCollapsed
                ? <ChevronRight className="w-3.5 h-3.5" />
                : <ChevronDown className="w-3.5 h-3.5" />}
            </button>
          )}
        </span>

        {/* Number */}
        <span className="mt-0.5 text-sm font-medium text-muted-foreground w-6 shrink-0">
          {label}
        </span>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <Input
            value={nameValue}
            onChange={(e) => setNameValue(e.target.value)}
            onBlur={handleNameBlur}
            className="font-semibold mb-4 rounded-none shadow-none border-transparent bg-slate-50 shadow-none focus-visible:border-input focus-visible:bg-slate-50"
          />
          <DescriptionField
              sectionId={section.id}
              initialValue={section.description}
              context={section.description_context}
              contextOutput={section.description_ai_output}
              projectId={projectId}
              templateQueryKey={templateQueryKey}
            />

        </div>
      </div>

      {/* Sources */}
      <SectionSourcesSummary
        documents={documents}
        extractionPoints={extractionPoints}
        artifacts={artifacts}
        gridReview={gridReview}
        sectionSources={sectionSources}
        onOpenSources={onOpenSources}
        className="w-44 shrink-0 pt-0.5"
      />

      {/* Extra column (e.g. AI Instructions) */}
      {extraColumn !== undefined && (
        <div className="w-80 shrink-0 flex flex-col gap-1.5 pt-0.5">{extraColumn}</div>
      )}

      {/* Actions */}
      {!hideActions && (
        <div className="w-40 flex items-center justify-end gap-1 shrink-0">
          <Button size="icon" variant="ghost" className="h-7 w-7" disabled={isFirst} onClick={onMoveUp} title="Move Up">
            <ChevronUp className="w-3.5 h-3.5" />
          </Button>
          <Button size="icon" variant="ghost" className="h-7 w-7" disabled={isLast} onClick={onMoveDown} title="Move Down">
            <ChevronDown className="w-3.5 h-3.5" />
          </Button>
          {!isChild && (
            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={onAddChild} title="Add subsection">
              <Plus className="w-3.5 h-3.5" />
            </Button>
          )}
          <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive hover:text-destructive" onClick={onDelete} title="Delete">
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      )}
    </div>
    {generatedContent && (
        <div className="border-b border-t bg-muted/30 px-4 py-3">{generatedContent}</div>
      )}
    </div>
  );
};
